import {
  Component,
  Input,
  ContentChildren,
  ViewChildren,
  Optional,
  Host,
  HostBinding,
  SkipSelf,
  isDevMode,
  TemplateRef,
  QueryList,
  AfterContentChecked,
  AfterViewChecked,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { animate, trigger, style, state, transition, AUTO_STYLE } from '@angular/animations';

import { PanelBarService } from './panelbar.service';
import { PanelBarContentDirective } from './panelbar-content.directive';
import { PanelBarItemTitleDirective } from './panelbar-item-title.directive';
import { PanelBarItemModel } from './panelbar-item-model';

import { Subscription } from 'rxjs';

/**
 * @hidden
 */
let nextId: number = 0;

/**
 * Represents the items of the Kendo UI PanelBar for Angular.
 */
@Component({
  standalone: false,
  animations: [
    trigger('toggle', [
      state('inactive', style({ display: 'none', height: '0px' })),
      transition('active => void', [animate(200, style({ display: 'block', height: '0px' }))]),
      transition('void => active', [
        style({ display: 'block', height: '0px' }),
        animate(200, style({ display: 'block', height: AUTO_STYLE })),
      ]),
      transition('inactive => active', [
        style({ display: 'block', height: '0px' }),
        animate(200, style({ display: 'block', height: AUTO_STYLE })),
      ]),
      transition('active => inactive', [animate(200, style({ display: 'none', height: '0px' }))]),
    ]),
  ],
  exportAs: 'kendoPanelbarItem',
  selector: 'kendo-panelbar-item',
  template: `<span
      #header
      [class.k-link]="true"
      [class.k-header]="!parent"
      [class.k-selected]="!disabled && selected"
      [class.k-state-focused]="!disabled && focused && wrapperFocused"
      (click)="onItemAction()">
      <span
        *ngIf="hasChildItems || hasContent"
        class="fa"
        [class.fa-caret-down]="!expanded"
        [class.fa-caret-up]="expanded"
        [class.k-panelbar-collapse]="expanded"
        [class.k-panelbar-expand]="!expanded">
      </span>
      <span *ngIf="icon || iconClass" class="k-icon" [ngClass]="iconClasses"> </span>
      <img *ngIf="imageUrl" class="k-image" [src]="imageUrl" alt="" />
      {{ title }}
      <ng-template [ngTemplateOutlet]="titleTemplate"></ng-template>
    </span>
    <div
      *ngIf="keepContent || (!disabled && expanded && (hasChildItems || hasContent))"
      [@toggle]="state"
      [style.overflow]="'hidden'"
      [attr.role]="'group'"
      [attr.aria-hidden]="!disabled && !expanded">
      <div
        *ngIf="hasChildItems && !items?.length"
        [style.overflow]="contentOverflow"
        [style.height]="contentHeight"
        class="k-panel k-group">
        <ng-content select="kendo-panelbar-item"></ng-content>
      </div>
      <div
        *ngIf="hasContent && !content"
        [style.overflow]="contentOverflow"
        [style.height]="contentHeight"
        class="k-content">
        <ng-template
          [ngTemplateOutlet]="contentTemplate.first.templateRef"
          [ngTemplateOutletContext]="{
            $implicit: {
              title: title,
              id: id,
              icon: icon,
              imageUrl: imageUrl,
              disabled: disabled,
              content: content,
            },
          }">
        </ng-template>
      </div>
      <div
        *ngIf="items?.length"
        [style.overflow]="contentOverflow"
        [style.height]="contentHeight"
        class="k-panel k-group">
        <kendo-panelbar-item
          *ngFor="let item of items"
          [title]="item.title"
          [id]="item.id"
          [icon]="item.icon"
          [iconClass]="item.iconClass"
          [imageUrl]="item.imageUrl"
          [selected]="!!item.selected"
          [expanded]="!!item.expanded"
          [disabled]="!!item.disabled"
          [template]="template"
          [items]="item.children"
          [content]="item.content">
        </kendo-panelbar-item>
      </div>
      <div *ngIf="content" [style.overflow]="contentOverflow" [style.height]="contentHeight" class="k-content">
        <ng-template
          [ngTemplateOutlet]="template"
          [ngTemplateOutletContext]="{
            $implicit: {
              title: title,
              id: id,
              icon: icon,
              imageUrl: imageUrl,
              disabled: disabled,
              content: content,
            },
          }">
        </ng-template>
        <ng-template [ngIf]="!template">{{ content }}</ng-template>
      </div>
    </div>`,
})
export class PanelBarItemComponent implements AfterContentChecked, AfterViewChecked, OnDestroy {
  /**
   * Sets the title of the PanelBar item.
   */
  @Input() public title: string = 'Untitled';

  /**
   * Allows the component to set the `"id"` property to each item.
   * Used to set the `id` attributes of the nested elements and to enable the WAI-ARIA support.
   */
  @Input() public id: string = `default-${nextId++}`;

  /**
   * Defines the icon that will be rendered next to the title.
   */
  @Input() public icon: string = '';

  /**
   * Defines  the icon that will be rendered next to the title by using a custom CSS class.
   */
  @Input() public iconClass: string = '';

  /**
   * Defines the location of the image that will be displayed next to the title.
   */
  @Input() public imageUrl: string = '';

  /**
   * When set to `true`, disables a PanelBar item.
   */
  @Input() public disabled: boolean = false;

  /**
   * When set to `true`, expands the PanelBar item.
   */
  @Input() set expanded(value: boolean) {
    let activeState = this.animate ? 'active' : 'activeWithoutAnimation';

    this.state = value ? activeState : 'inactive';

    this._expanded = value;
  }

  get expanded(): boolean {
    return this._expanded;
  }

  /**
   * Sets the selected state of a PanelBar item.
   */
  @Input() public selected: boolean = false;

  /**
   * Sets the content of the PanelBar item.
   * By design, it is used when the
   * [`items`]({% slug api_layout_panelbarcomponent %}#toc-items)
   * property of the PanelBar is set.
   */
  @Input() public content: any;

  /**
   * @hidden
   */
  @Input() public items: PanelBarItemModel[];

  /**
   * @hidden
   */
  @Input() public template: TemplateRef<any>;

  @ViewChild('header', {}) public header: ElementRef;

  public contentHeight: string;
  public contentOverflow: string;
  public keepContent: boolean = false;
  public childrenItems: PanelBarItemComponent[];
  public hasChildItems: boolean = false;
  public hasContent: boolean = false;
  public state: string = 'inactive';
  public animate: boolean = false;

  @HostBinding('attr.role') public role: string = 'treeitem';
  @HostBinding('attr.title') public titleAttribute: string = null;

  @HostBinding('class.k-item')
  public get kItemClass(): boolean {
    return true;
  }

  @HostBinding('class.k-state-default')
  public get kStateDefaultClass(): boolean {
    return !this.disabled;
  }

  @HostBinding('class.k-state-disabled')
  public get kStateDisabledClass(): boolean {
    return this.disabled;
  }

  @HostBinding('class.k-state-expanded')
  public get kStateExpandedClass(): boolean {
    return !this.disabled && this.expanded && (this.hasChildItems || this.hasContent);
  }

  @HostBinding('id')
  get itemId(): string {
    return 'k-panelbar-' + this.eventService.pbId + '-item-' + this.id;
  }

  @HostBinding('attr.aria-expanded')
  get ariaExpanded(): boolean {
    return this.hasChildItems || this.hasContent ? !this.disabled && this.expanded : null;
  }

  @HostBinding('attr.aria-selected')
  get ariaSelected(): boolean {
    return !this.disabled && this.selected;
  }

  @HostBinding('attr.aria-disabled')
  get ariaDisabled(): boolean {
    return this.disabled ? true : null;
  }

  /**
   * @hidden
   */
  get titleTemplate(): TemplateRef<any> {
    return this.titleTemplates.length > 0 ? this.titleTemplates.toArray()[0].templateRef : undefined;
  }

  @ViewChildren(PanelBarItemComponent) public viewChildItems: QueryList<PanelBarItemComponent>;
  @ContentChildren(PanelBarItemComponent) public contentItems: QueryList<PanelBarItemComponent>;
  // ContentChild does not support descendants property, so we use ContentChildren for contentTemplate instead
  @ContentChildren(PanelBarContentDirective, { descendants: false })
  public contentTemplate: QueryList<PanelBarContentDirective>;
  @ContentChildren(PanelBarItemTitleDirective, { descendants: false })
  public titleTemplates: QueryList<PanelBarItemTitleDirective>;

  public focused: boolean = false;
  public wrapperFocused: boolean = false;

  protected subscriptions: Subscription = new Subscription(() => {
    /**/
  });

  constructor(
    @SkipSelf() @Host() @Optional() public parent: PanelBarItemComponent,
    private eventService: PanelBarService<PanelBarItemComponent>,
  ) {
    this.subscriptions.add(eventService.parent$.subscribe((focused) => this.onWrapperFocusChange(focused)));
    this.subscriptions.add(eventService.keepContent$.subscribe((keepContent) => (this.keepContent = keepContent)));

    this.wrapperFocused = parent ? parent.focused : false;
  }

  /**
   * @hidden
   */
  public headerHeight(): number {
    return this.header.nativeElement.offsetHeight;
  }

  /**
   * @hidden
   */
  public ngAfterContentChecked(): void {
    this.hasChildItems = this.contentItems.length > 1 || (this.items && this.items.length > 0);

    this.hasContent =
      (this.contentTemplate !== undefined && this.contentTemplate.length > 0) || this.content !== undefined;

    this.validateConfiguration();
  }

  /**
   * @hidden
   */
  public ngAfterViewChecked(): void {
    if (this.items) {
      this.childrenItems = this.viewChildItems.toArray();
    } else {
      this.childrenItems = this.contentItems.filter((item) => item !== this);
    }
  }

  /**
   * @hidden
   */
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * @hidden
   */
  public onItemAction(): void {
    if (!this.disabled) {
      this.eventService.onSelect(this);
    }
  }

  /**
   * @hidden
   */
  public get iconClasses(): any {
    let icon: string = this.icon ? 'k-i-' + this.icon : null;

    return {
      [icon || this.iconClass]: true,
    };
  }

  /**
   * @hidden
   */
  public serialize(): PanelBarItemModel {
    return <PanelBarItemModel>{
      content: this.content as string,
      disabled: this.disabled as boolean,
      expanded: this.expanded as boolean,
      focused: this.focused as boolean,
      icon: this.icon as string,
      iconClass: this.iconClass as string,
      id: this.id as string,
      imageUrl: this.imageUrl as string,
      selected: this.selected as boolean,
      title: this.title as string,
    };
  }

  /**
   * @hidden
   */
  public subTreeViewItems(): PanelBarItemComponent[] {
    let subTree: PanelBarItemComponent[] = [];

    this.viewChildItems.forEach((item) => {
      subTree = subTree.concat(item.subTreeViewItems());
      subTree.push(item);
    });

    return subTree;
  }

  /**
   * @hidden
   */
  public validateConfiguration(): void {
    if (isDevMode()) {
      if (this.content && this.contentTemplate !== undefined && this.contentTemplate.length > 0) {
        throw new Error('Invalid configuration: mixed template components and component property.');
      }
    }
  }

  private _expanded: boolean = false;

  private onWrapperFocusChange(focused: boolean): void {
    this.wrapperFocused = focused;
  }
}
